import { ColumnDef } from '@tanstack/react-table';
import constants from 'constants/constants';
import moment from 'moment-timezone';
import colorsVars from 'styles/colors.module.scss';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import Icon from 'components/common/Icons/base/Icon/Icon';
import { ErrorCell } from 'components/common/Tables/Cells/ErrorCell/ErrorCell';
import { TooltipCellWithCopy } from 'components/common/Tables/Cells/TooltipCellWithCopy/TooltipCellWithCopy';
import { columnsCommonConfig } from 'components/common/Tables/columnsCommonConfig';
import { IProgramVersion } from 'components/common/VersionCompareSelectorSection/VersionCompareSelectorWrapper/VersionCompareSelectorWrapper';
import { Pills } from 'components/common/pills/pills';

import { CustomizedTooltip } from 'components/common';
import { RowOptionsCell } from 'components/widgets/RenderLibraryTable/components/RowOptionsCell/rowOptionsCell';
import { RowOptionsHeader } from 'components/widgets/RenderLibraryTable/components/RowOptionsHeader/rowOptionsHeader';
import classes from 'components/widgets/RenderLibraryTable/libraryTable.module.scss';

const { programLibraryGreen, white, fileIconColor, black } = colorsVars;

export const DESIGNATED_BASELINE = {
    id: 'isDesignatedBaseline',
    value: 'D.Baseline',
    title: 'Designated Baseline',
};

export const IS_LATEST = {
    id: 'isLatest',
    value: 'Latest',
    title: 'Latest',
};

export const fileTypes = {
    XER: 'Primavera XER',
    PLF: 'Primavera XER + PLF',
    MPP: 'MS Project',
    PW: 'Planisware',
    EXT: 'Integrated Project',
};

export const columnsV8: ColumnDef<IProgramVersion>[] = [
    {
        accessorKey: 'fileType',
        header: () => <span className={classes.typeHeader}>Type</span>,
        meta: {
            columnLabel: 'Type',
        },
        size: 60,
        cell: ({ row, getValue }) => {
            const value = getValue<IProgramVersion['fileType']>();
            const status = row.original.status;
            const icon = row.original.projectType === 'EXT' ? 'integrated-file' : value;
            const fileType = row.original.projectType === 'EXT' ? 'EXT' : value;

            return (
                <CustomizedTooltip
                    tooltipPosition={TooltipPosition.Top}
                    tooltipContent={fileTypes[fileType]}
                    triggerElement={
                        <Icon name={icon} size={'3.5rem'} color={status === 'OK' ? fileIconColor : black} />
                    }
                />
            );
        },
    },
    {
        header: () => <span className={classes.typeHeader}>Sub-Program</span>,
        meta: {
            columnLabel: 'Sub Program',
        },
        accessorKey: 'contractName',
        enableColumnFilter: true,
        ...columnsCommonConfig.contractName,
    },
    {
        header: () => <span className={classes.typeHeader}>Source File</span>,
        meta: {
            columnLabel: 'Source File',
        },
        accessorKey: 'sourceFile',
        enableColumnFilter: true,
        size: 200,
        cell: ({ getValue }) => <TooltipCellWithCopy text={getValue()} />,
    },
    {
        header: () => <span className={classes.typeHeader}>Version Name</span>,
        meta: {
            columnLabel: 'Version Name',
        },
        accessorKey: 'versionName',
        enableColumnFilter: true,
        ...columnsCommonConfig.versionName,
        cell: ({ getValue }) => <TooltipCellWithCopy text={getValue()} />,
    },
    {
        header: () => <span className={classes.typeHeader}>Version Date</span>,
        accessorKey: 'versionDate',
        enableColumnFilter: true,
        ...columnsCommonConfig.versionDate,
        meta: {
            columnLabel: 'Version Date',
            isFixedSize: true,
        },
        cell: ({ getValue }) =>
            moment(getValue<IProgramVersion['versionDate']>()).format(constants.formats.date.default),
        filterFn: (row, columnId, filterValue) => {
            const value = row.getValue<IProgramVersion['versionDate']>(columnId);
            const rowValue = moment(value).format(constants.formats.date.default);
            return rowValue.includes(filterValue);
        },
    },
    {
        header: () => <span className={classes.typeHeader}>Uploaded By</span>,
        accessorKey: 'uploadingUser',
        enableColumnFilter: true,
        size: 200,
        meta: {
            isFixedSize: true,
            columnLabel: 'Uploaded By',
        },
        cell: ({ getValue, row, column }) => {
            const { status, errorMessage } = row.original;
            const isError = status.toLowerCase() === 'error';
            column.colSpan = isError ? 3 : 1;

            return isError ? <ErrorCell error={errorMessage} /> : <TooltipCellWithCopy text={getValue()} />;
        },
    },
    {
        header: () => <span className={classes.typeHeader}>Upload Date</span>,
        accessorKey: 'uploadedDate',
        enableColumnFilter: true,
        size: 200,
        meta: {
            isFixedSize: true,
            columnLabel: 'Upload Date',
        },
        cell: ({ getValue, row }) => {
            const isError = row.original.status.toLowerCase() === 'error';
            return isError
                ? null
                : moment(getValue<IProgramVersion['uploadedDate']>()).format(constants.formats.date.defaultWithTime);
        },
        filterFn: (row, columnId, filterValue) => {
            const value = row.getValue<IProgramVersion['versionDate']>(columnId);
            const rowValue = moment(value).format(constants.formats.date.defaultWithTime);
            return rowValue.includes(filterValue);
        },
    },
    {
        header: () => <span className={classes.typeHeader}>Re/Baseline</span>,
        id: 'baseline',
        enableColumnFilter: true,
        meta: {
            columnLabel: 'Re/Baseline',
            isFixedSize: true,
        },
        accessorFn: (row) => {
            if (row.isDesignatedBaseline) {
                return DESIGNATED_BASELINE.id;
            }
            if (row.isLatest) {
                return IS_LATEST.id;
            }
            return 'isBaseline';
        },
        size: 130,
        cell: ({ getValue, row }) => {
            const getPillContent = (value) => {
                if (value === DESIGNATED_BASELINE.id) {
                    return { value: DESIGNATED_BASELINE.value, title: DESIGNATED_BASELINE.title };
                }
                if (value === IS_LATEST.id) {
                    return { value: IS_LATEST.value, title: IS_LATEST.title };
                }
                return null;
            };
            const pillContent = getPillContent(getValue());
            const isBaselineContent = row.original.isBaseline ? 'Yes' : 'No';
            const isError = row.original.status.toLowerCase() === 'error';
            if (isError) return null;
            return pillContent ? (
                <Pills
                    style={{
                        background: programLibraryGreen,
                        color: white,
                        fontSize: '1.6rem',
                    }}
                >
                    <CustomizedTooltip
                        tooltipPosition={TooltipPosition.Top}
                        tooltipContent={pillContent.title}
                        triggerElement={pillContent.value}
                    />
                </Pills>
            ) : (
                isBaselineContent
            );
        },
        filterFn: (row, columnId, filterValue) => {
            const getRowValue = ({ row, accessorId }) => {
                if (accessorId === DESIGNATED_BASELINE.id) {
                    return DESIGNATED_BASELINE.value;
                }
                if (accessorId === IS_LATEST.id) {
                    return IS_LATEST.value;
                }
                return row.original.isBaseline ? 'Yes' : 'No';
            };
            const rowValue = getRowValue({ row, accessorId: row.getValue(columnId) });
            return rowValue.toLocaleLowerCase().includes(filterValue.toLocaleLowerCase());
        },
    },
    {
        header: ({ table }) => {
            const { getToggleAllRowsSelectedHandler, getIsAllRowsSelected, getSelectedRowModel } = table;
            return (
                <RowOptionsHeader
                    toggleAllRowsSelected={getToggleAllRowsSelectedHandler()}
                    isAllRowsSelected={getIsAllRowsSelected()}
                    selectedFlatRows={getSelectedRowModel().flatRows}
                />
            );
        },
        accessorFn: () => null,
        enableColumnFilter: false,
        enableSorting: false,
        ...columnsCommonConfig.rowOptions,
        size: 200,
        meta: {
            isFixedSize: true,
        },
        cell: ({ row }) => {
            const { index, original, getIsSelected, getToggleSelectedHandler } = row;
            const { status, errorMessage, id, versionHashCode, sourceFile, versionName } = original;
            return (
                <RowOptionsCell
                    index={index}
                    status={status}
                    errorMessage={errorMessage}
                    id={id}
                    sourceFile={sourceFile}
                    versionHashCode={versionHashCode}
                    isSelected={getIsSelected()}
                    toggleRowSelected={getToggleSelectedHandler()}
                    versionName={versionName}
                />
            );
        },
    },
];
